import Catalyst from '@Services/Catalyst';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import { NOTIFICATION_DIALOG_TYPES } from '@Containers/Dialogs/DialogsMapperConstants';
import store from '@Store';

class NotifyDialogsService {
  /**
   * main method to display standard notification modal dialog like it was in Angular RxNotifyModalService.openModalWindow
   * @param options: following options can be passed:
   * title: 'Dialog header text',
   * text: 'Dialog body text or <element>',
   * type: one of 'error', 'warning', 'info', 'success'
   * confirmText: 'Yes or Confirm button text
   * cancelText: 'Cancel button text' if needed 2 buttons confirmation dialog
   * onConfirm: confirm or Yes callback
   * layoutType: 'error', 'info', 'question', 'success' for confirmation 2 buttons dialog
   * trackingOptions: object containing {message, component, prefix} for AA tracking on dialog open
   * traceparent: debug info - only for error popup
   * timestamp: debug info - only for error popup
   * link: object containing {url, linkText, preMessage, postMessage } - only for error popup
   * closeBtn: to show x close icon if needed
   * size: size of dialog box - sm, md, lg, xl, fit
   * crumb: breadcrumb object to create alert for 'error-alert' type modal
   */
  // eslint-disable-next-line class-methods-use-this
  openModalWindow(options) {
    let { title, text } = options;

    const {
      type,
      confirmText,
      html,
      cancelText,
      onConfirm,
      movable,
      trackingOptions,
      traceparent,
      timestamp,
      closeBtn,
      link,
      size,
      crumb,
    } = options;

    const dialogType = cancelText ? NOTIFICATION_DIALOG_TYPES.confirm : NOTIFICATION_DIALOG_TYPES[type];
    // for confirmation dialog we can have different layout types
    const layoutType = type === 'warning' ? 'error' : type; // no special warning layout type - just error

    if (trackingOptions) {
      Catalyst.trackError(
        {
          message: trackingOptions.message || options.text,
          component: trackingOptions.component,
        },
        trackingOptions.prefix
      );
    }

    const noHeader = !!(!title && text);
    if (noHeader) {
      // case with no header popup in angular - put content to header to be consistent
      title = text;
      text = undefined;
    }
    store.dispatch(
      updateDialog({
        id: dialogType,
        dialogType,
        movable: movable || false,
        header: title,
        content: text,
        html,
        yesLabel: confirmText,
        cancelLabel: cancelText,
        onYesClick: onConfirm,
        layoutType, // only for confirm dialog
        traceInfo: {
          // only for error dialog
          traceparent,
          timestamp,
        },
        link, // only for error dialog
        closeBtn: closeBtn || false, // do not show close button by default to be consistent with angular popups
        noContent: noHeader,
        size,
        crumb, // only for error-alert dialog
        ...(options.onSelfClose ? { onSelfClose: options.onSelfClose } : {}),
      })
    );
  }

  /**
   * method to display standard notification modal dialog like it was in Angular RxNotifyModalService.openModalWindow
   * with Promise to allow call .then() when popup is closed
   * @param options
   * @returns {Promise<unknown>}
   */
  openModalWindowPromise(options) {
    return new Promise((resolve) => {
      this.openModalWindow({
        ...options,
        onSelfClose: resolve,
        onConfirm: () => {
          if (options.onConfirm) {
            options.onConfirm();
          }
          // add promise resolve call on close
          resolve();
        },
      });
    });
  }
}

export default new NotifyDialogsService();
